//@flow
import React from 'react';
import {Link} from 'react-router-dom';
import {makeStyles, Typography} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import {LocalMall} from '@material-ui/icons';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {timeAgo} from '../../../../utils/date/timeAgo';
import {useTrainBotActions} from '../../hooks';
import {useToastActions} from '../../../toast-alert/utils';
import type {StoreChatbot} from '../../../entities';

const useStyles = makeStyles(theme => ({
  updateContainer: {
    background: 'linear-gradient(to bottom, #f4f0ff, #ffffff)',
    borderRadius: '8px',
    padding: '1rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '.75rem',

    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  icon: {
    color: colors.primary,
    marginBottom: '0.5rem',
  },
  label: {
    fontWeight: 600,
    color: colors.grey800,
    marginBottom: '0.5rem',
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey800,
    maxWidth: '600px',
  },
  lastUpdated: {
    marginTop: '0.5rem',
    fontSize: '0.875rem',
    color: colors.grey600,
    fontStyle: 'italic',
    fontWeight: 500,
  },
  submitButton: {
    maxWidth: '250px',
    marginTop: '1.5rem',
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 600,
    marginTop: '1rem',
    fontSize: '0.875rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
  lastStoredTraining?: {
    updatedAt: string,
  },
};

const TrainingTabProducts = React.memo(function TrainingTabProducts(
  props: Props
) {
  const classes = useStyles();
  const {showToast} = useToastActions();
  const {trainBot, loading} = useTrainBotActions();

  const handleTrain = async () => {
    if (!props.chatBot?.id) return;

    try {
      await trainBot(props.chatBot.id, {
        trainingType: 'PRODUCTS',
      });

      showToast({
        message:
          'Entrenamiento con catálogo de productos iniciado correctamente',
        title: 'Entrenamiento iniciado',
        type: 'success',
        duration: 3000,
      });

      props.onSaveSuccess();
    } catch (error) {
      const errorMessage =
        error?.message || 'Error desconocido durante el entrenamiento';
      showToast({
        message: errorMessage,
        title: 'Error en el entrenamiento',
        type: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <div className={classes.updateContainer}>
      <div className={classes.content}>
        <span className={classes.icon}>
          <LocalMall style={{fontSize: 48}} />
        </span>
        <span className={classes.label}>Entrena tu bot con tus productos</span>
        <Typography variant="caption" className={classes.caption}>
          Al sincronizar tu catálogo, el bot podrá responder preguntas sobre
          precios, descripciones, disponibilidad y características de todos tus
          productos activos.
        </Typography>

        {props.lastStoredTraining && (
          <Typography
            variant="caption"
            className={classes.caption}
            style={{
              marginTop: '0.5rem',
              fontStyle: 'italic',
              fontWeight: 500,
              color: colors.grey600,
              display: 'block',
            }}
          >
            Última actualización:{' '}
            {timeAgo(new Date(props.lastStoredTraining.updatedAt).getTime())}
          </Typography>
        )}

        <CustomButton
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={handleTrain}
          disabled={loading}
          fullWidth
        >
          {loading ? 'Actualizando productos...' : 'Actualizar'}
        </CustomButton>

        <Link to="/products" className={classes.link}>
          Ver mis productos →
        </Link>
      </div>
    </div>
  );
});

export default TrainingTabProducts;
